import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import DashboardLayout from './components/dashboard/DashboardLayout';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import RequireRoles from './components/RequireRoles';
import { COACHING_CHAT_ROLES } from './lib/access.config';

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Authentication pages

const Login = Loadable(lazy(() => import('./pages/authentication/Login')));

// Dashboard pages

const Account = Loadable(lazy(() => import('./pages/dashboard/Account')));
const AdminHome = Loadable(lazy(() => import('./pages/dashboard/AdminHome')));
const AdminUsersList = Loadable(lazy(() => import('./pages/dashboard/AdminUsersList')));
const AllCustomerList = Loadable(lazy(() => import('./pages/dashboard/AllCustomerList')));
const MiscAdmin = Loadable(lazy(() => import('./pages/dashboard/MiscAdmin')));
const SubsPausedCanceled = Loadable(lazy(() => import('./pages/dashboard/misc-admin/SubsPausedCanceled')));
const AsyncConsultationDetails = Loadable(lazy(() => import('./pages/dashboard/consultations/AsyncConsultationDetails')));
const AsyncConsultationsList = Loadable(lazy(() => import('./pages/dashboard/consultations/AsyncConsultationsList')));
const AuroraAuditList = Loadable(lazy(() => import('./pages/dashboard/AuroraAuditList')));
const AuthorizationRequired = Loadable(lazy(() => import('./pages/AuthorizationRequired')));
const BIOrders = Loadable(lazy(() => import('./pages/dashboard/BIOrders')));
const BIFacebook = Loadable(lazy(() => import('./pages/dashboard/BIFacebook')));
const ClinicianList = Loadable(lazy(() => import('./pages/dashboard/ClinicianList')));
const Connect = Loadable(lazy(() => import('./pages/dashboard/Connect')));
const ConsultationDetailsView = Loadable(lazy(() => import('./pages/dashboard/ConsultationDetailsView')));
const ConsultationsList = Loadable(lazy(() => import('./pages/dashboard/ConsultationsList')));
const ConsultationWaitlistList = Loadable(lazy(() => import('./pages/dashboard/ConsultationWaitlistList')));
const StuckConsultationsList = Loadable(lazy(() => import('./pages/dashboard/StuckConsultationsList')));
const CoachingPatients = Loadable(lazy(() => import('./pages/dashboard/CoachingPatients')));
const CoachingSessionManagement = Loadable(lazy(() => import('./pages/dashboard/CoachingSessionManagement')));
const GroupSessionManagement = Loadable(lazy(() => import('./pages/dashboard/GroupSessionManagement')));
const ChatsList = Loadable(lazy(() => import('./pages/dashboard/ChatsList')));
const ChatConfigs = Loadable(lazy(() => import('./pages/dashboard/ChatConfigs')));
const ClinicianChatHolds = Loadable(lazy(() => import('./pages/dashboard/ClinicianChatHolds')));
const EmailTester = Loadable(lazy(() => import('./pages/dashboard/EmailTester')));
const ChatTools = Loadable(lazy(() => import('./pages/dashboard/ChatTools')));
const CustomerDetails = Loadable(lazy(() => import('./pages/dashboard/CustomerDetails')));
const CustomerEdit = Loadable(lazy(() => import('./pages/dashboard/CustomerEdit')));
const CustomerGenerateCheckout = Loadable(lazy(() => import('./pages/dashboard/CustomerGenerateCheckout')));
const CustomerList = Loadable(lazy(() => import('./pages/dashboard/CustomerList')));
const CustomerServiceDashboard = Loadable(lazy(() => import('./pages/dashboard/CustomerServiceDashboard')));
const LiveSalesDashboard = Loadable(lazy(() => import('./pages/dashboard/LiveSalesDashboard')));
const DoctorRequests = Loadable(lazy(() => import('./pages/dashboard/DoctorRequests')));
const EHROverview = Loadable(lazy(() => import('./pages/dashboard/EHROverview')));
const EHRAppointmentsQueue = Loadable(lazy(() => import('./pages/dashboard/EHRAppointmentsQueue')));
const EHRMemosQueue = Loadable(lazy(() => import('./pages/dashboard/EHRMemosQueue')));
const EHRNotesQueue = Loadable(lazy(() => import('./pages/dashboard/EHRNotesQueue')));
const EHRPatientsList = Loadable(lazy(() => import('./pages/dashboard/EHRPatientsList')));
const EHRPatientsQueue = Loadable(lazy(() => import('./pages/dashboard/EHRPatientsQueue')));
const EHRQueueLog = Loadable(lazy(() => import('./pages/dashboard/EHRQueueLog')));
const IcebergPatients = Loadable(lazy(() => import('./pages/dashboard/IcebergPatients')));
const InsuranceDetailsEditForm = Loadable(lazy(() => import('./components/dashboard/customer/InsuranceDetailsEditForm')));
const InsuranceEligibilityEditForm = Loadable(lazy(() => import('./components/dashboard/customer/InsuranceEligibilityEditForm')));
const KetSubscriptionManage = Loadable(lazy(() => import('./pages/dashboard/KetSubscriptionManage')));
const LeadsView = Loadable(lazy(() => import('./pages/dashboard/LeadsKanban')));
const NMICouponCreate = Loadable(lazy(() => import('./pages/dashboard/NMICouponCreate')));
const NMICouponDetails = Loadable(lazy(() => import('./pages/dashboard/NMICouponDetails')));
const NMICouponEdit = Loadable(lazy(() => import('./pages/dashboard/NMICouponEdit')));
const NMICoupons = Loadable(lazy(() => import('./pages/dashboard/NMICoupons')));
const NMIOrderDetails = Loadable(lazy(() => import('./pages/dashboard/NMIOrderDetails')));
const NMIOrderRefund = Loadable(lazy(() => import('./pages/dashboard/NMIOrderRefund')));
const NMIOrders = Loadable(lazy(() => import('./pages/dashboard/NMIOrders')));
const NMIPlanCreate = Loadable(lazy(() => import('./pages/dashboard/NMIPlanCreate')));
const NMIPlanDetails = Loadable(lazy(() => import('./pages/dashboard/NMIPlanDetails')));
const NMIPlanEdit = Loadable(lazy(() => import('./pages/dashboard/NMIPlanEdit')));
const NMIPlans = Loadable(lazy(() => import('./pages/dashboard/NMIPlans')));
const NMICollections = Loadable(lazy(() => import('./pages/dashboard/NMICollections')));
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const NpsSurveys = Loadable(lazy(() => import('./pages/dashboard/NpsSurveys')));
const OpsView = Loadable(lazy(() => import('./pages/dashboard/OpsView')));
const P2PReferralsList = Loadable(lazy(() => import('./pages/dashboard/P2PReferralsList')));
const PurchasesList = Loadable(lazy(() => import('./pages/dashboard/PurchasesList')));
const FormSubmissionsList = Loadable(lazy(() => import('./pages/dashboard/FormSubmissionsList')));
const IopEpisodePage = Loadable(lazy(() => import('./pages/dashboard/IopEpisodePage')));
const IopPage = Loadable(lazy(() => import('./pages/dashboard/IopPage')));
const IopCohortDetails = Loadable(lazy(() => import('./pages/dashboard/IopCohortDetails')));
const IopSessionDetails = Loadable(lazy(() => import('./pages/dashboard/IopSessionDetails')));
const IopPatientSessionPage = Loadable(lazy(() => import('./pages/dashboard/IopPatientSessionPage')));
const FunnelBrowser = Loadable(lazy(() => import('./pages/dashboard/FunnelBrowser')));
const FormVersionDetail = Loadable(lazy(() => import('./pages/dashboard/FormVersionDetail')));
const SalesList = Loadable(lazy(() => import('./pages/dashboard/SalesList')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));
const SquareupOrders = Loadable(lazy(() => import('./pages/dashboard/SquareupOrders')));
const SquareupPlans = Loadable(lazy(() => import('./pages/dashboard/SquareupPlans')));
const StripeCoupons = Loadable(lazy(() => import('./pages/dashboard/StripeCoupons')));
const StripeOrders = Loadable(lazy(() => import('./pages/dashboard/StripeOrders')));
const StripePlans = Loadable(lazy(() => import('./pages/dashboard/StripePlans')));
const SupportedStates = Loadable(lazy(() => import('./pages/dashboard/SupportedStates')));
const SupportGroupSurveyList = Loadable(lazy(() => import('./pages/dashboard/SupportGroupSurveyList')));
const TestKitsList = Loadable(lazy(() => import('./pages/dashboard/TestKitsList')));
const TherapistDetails = Loadable(lazy(() => import('./pages/dashboard/TherapistDetails')));
const UpcomingInsuranceConsults = Loadable(lazy(() => import('./pages/dashboard/UpcomingInsuranceConsults')));
const UpcomingNaxRefills = Loadable(lazy(() => import('./pages/dashboard/UpcomingNaxRefills')));
const UxSurveySubmissionsList = Loadable(lazy(() => import('./pages/dashboard/UxSurveySubmissionsList')));
const ScheduledActionsList = Loadable(lazy(() => import('./pages/dashboard/ScheduledActionsList')));
const PrescriptionBalanceList = Loadable(lazy(() => import('./pages/dashboard/PrescriptionBalanceList')));
const TestimonialInvites = Loadable(lazy(() => import('./pages/dashboard/TestimonialInvites')));
const TestimonialsReview = Loadable(lazy(() => import('./pages/dashboard/TestimonialsReview')));
const ContentCaptureRequests = Loadable(lazy(() => import('./pages/dashboard/ContentCaptureRequests')));
const ContentCaptureRequestDetail = Loadable(lazy(() => import('./pages/dashboard/ContentCaptureRequestDetail')));
const ContentCaptureLibrary = Loadable(lazy(() => import('./pages/dashboard/ContentCaptureLibrary')));
const ContentCaptureMy = Loadable(lazy(() => import('./pages/dashboard/ContentCaptureMy')));
const ContentCaptureRecorder = Loadable(lazy(() => import('./pages/dashboard/ContentCaptureRecorder')));
const WalnutBillDetails = Loadable(lazy(() => import('./pages/dashboard/WalnutBillDetails')));
const WalnutBillRefund = Loadable(lazy(() => import('./pages/dashboard/WalnutBillRefund')));
const WalnutBills = Loadable(lazy(() => import('./pages/dashboard/WalnutBills')));
const WalnutCouponCreate = Loadable(lazy(() => import('./pages/dashboard/WalnutCouponCreate')));
const WalnutCouponDetails = Loadable(lazy(() => import('./pages/dashboard/WalnutCouponDetails')));
const WalnutCouponEdit = Loadable(lazy(() => import('./pages/dashboard/WalnutCouponEdit')));
const WalnutCoupons = Loadable(lazy(() => import('./pages/dashboard/WalnutCoupons')));
const WalnutPlanCreate = Loadable(lazy(() => import('./pages/dashboard/WalnutPlanCreate')));
const WalnutPlanDetails = Loadable(lazy(() => import('./pages/dashboard/WalnutPlanDetails')));
const WalnutPlanEdit = Loadable(lazy(() => import('./pages/dashboard/WalnutPlanEdit')));
const WalnutPlans = Loadable(lazy(() => import('./pages/dashboard/WalnutPlans')));
const DashboardCancelEvents = Loadable(lazy(() => import('./pages/dashboard/DashboardCancelEvents')));
const LinkShortener = Loadable(lazy(() => import('./pages/dashboard/LinkShortener')));
const KnowledgeBase = Loadable(lazy(() => import('./pages/dashboard/KnowledgeBase')));
const ContentCreation = Loadable(lazy(() => import('./pages/dashboard/ContentCreation')));
const BrainRoutines = Loadable(lazy(() => import('./pages/dashboard/BrainRoutines')));
const routes = [
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        )
      }
    ]
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="home" />
      },
      {
        path: 'home',
        element: <AdminHome />
      },
      {
        path: 'base-camp',
        element: <Navigate to="/dashboard/home?tab=base-camp" replace />
      },
      {
        path: 'account',
        element: <Account />
      },
      {
        path: 'customers',
        children: [
          {
            path: '',
            element: <CustomerList />
          },
          {
            path: 'ops-view',
            element: <OpsView />
          },
          {
            path: 'upcoming-insurance-consults',
            element: <UpcomingInsuranceConsults />
          },
          {
            path: 'upcoming-nax-refills',
            element: <UpcomingNaxRefills />
          },
          {
            path: 'leads-view',
            element: <LeadsView />
          },
          {
            path: 'all',
            element: <AllCustomerList />
          },
          {
            path: ':customerId',
            element: <CustomerDetails />
          },
          {
            path: ':customerId/edit',
            element: <CustomerEdit />
          },
          {
            path: ':customerId/insurance-eligibility/edit',
            element: <InsuranceEligibilityEditForm />
          },
          {
            path: ':customerId/insurance-details/edit',
            element: <InsuranceDetailsEditForm />
          },
          {
            path: ':customerId/generate-checkout',
            element: <CustomerGenerateCheckout />
          },
          {
            path: ':customerId/purchases/:purchaseId/async-consultation',
            element: <AsyncConsultationDetails />
          },
          {
            path: ':customerId/purchases/:purchaseId/consultation',
            element: <ConsultationDetailsView />
          }
        ]
      },
      {
        path: 'doctor-requests',
        element: <DoctorRequests />
      },
      {
        path: 'customer-service',
        element: (
          <RequireRoles roles={['admin', 'super-admin']}>
            <CustomerServiceDashboard />
          </RequireRoles>
        )
      },
      {
        path: 'admin-users',
        element: <AdminUsersList />
      },
      {
        path: 'brain-routines',
        element: (
          <RequireRoles roles={['super-admin']}>
            <BrainRoutines />
          </RequireRoles>
        )
      },
      {
        path: 'clinicians',
        element: <ClinicianList />
      },
      {
        path: 'supported-states',
        element: <SupportedStates />
      },
      {
        path: 'prescription-balance',
        element: <PrescriptionBalanceList />
      },
      {
        path: 'admin',
        children: [
          {
            path: '',
            element: <MiscAdmin />
          },
          {
            path: 'subs-paused-canceled',
            element: <SubsPausedCanceled />
          }
        ]
      },
      {
        path: 'chats',
        element: <ChatsList />
      },
      {
        path: 'chats/tools',
        element: (
          <RequireRoles roles={['admin', 'super-admin']}>
            <ChatTools />
          </RequireRoles>
        )
      },
      {
        path: 'chat-configs',
        element: <ChatConfigs />
      },
      {
        path: 'clinician-chat-holds',
        element: <ClinicianChatHolds />
      },
      {
        path: 'email-tester',
        element: (
          <RequireRoles roles={['admin', 'super-admin']}>
            <EmailTester />
          </RequireRoles>
        )
      },
      {
        path: 'support-group-surveys',
        element: <SupportGroupSurveyList />
      },
      {
        path: 'ux-surveys',
        element: <UxSurveySubmissionsList />
      },
      {
        path: 'nps-surveys',
        element: <NpsSurveys />
      },
      {
        path: 'aurora-alerts',
        element: <Navigate to="/dashboard/chats?chatType=support&tab=alerts" replace />
      },
      {
        path: 'aurora-audit',
        element: <AuroraAuditList />
      },
      {
        path: 'dashboard-cancel-events',
        element: <DashboardCancelEvents />
      },
      {
        path: 'link-shortener',
        element: <LinkShortener />
      },
      {
        path: 'knowledge-base',
        element: <KnowledgeBase />
      },
      {
        path: 'content-creation',
        children: [
          {
            path: '',
            element: <ContentCreation />
          },
          {
            path: 'interviews/:interviewId',
            element: <ContentCreation />
          },
          {
            path: 'interviews/:interviewId/details',
            element: <ContentCreation />
          }
        ]
      },
      {
        path: 'connect',
        children: [
          {
            path: '',
            element: <Connect />
          },
          {
            path: ':therapistId',
            element: <TherapistDetails />
          }
        ]
      },
      {
        path: 'bi-orders',
        element: <BIOrders />
      },
      {
        path: 'bi-facebook',
        element: <BIFacebook />
      },
      {
        path: 'patients-sync-queue',
        children: [
          {
            path: '',
            element: <EHRPatientsQueue />
          },
          {
            path: ':queType/:recordId',
            element: <EHRQueueLog />
          }]
      },
      {
        path: 'appointments-sync-queue',
        children: [
          {
            path: '',
            element: <EHRAppointmentsQueue />
          },
          {
            path: ':queType/:recordId',
            element: <EHRQueueLog />
          }]
      },
      {
        path: 'notes-sync-queue',
        children: [
          {
            path: '',
            element: <EHRNotesQueue />
          },
          {
            path: ':queType/:recordId',
            element: <EHRQueueLog />
          }]
      },
      {
        path: 'memos-sync-queue',
        children: [
          {
            path: '',
            element: <EHRMemosQueue />
          },
          {
            path: ':queType/:recordId',
            element: <EHRQueueLog />
          }]
      },
      {
        path: 'ehr-patients',
        children: [
          {
            path: '',
            element: <EHRPatientsList />
          }
        ]
      },
      {
        path: 'ehr-overview',
        children: [
          {
            path: '',
            element: <EHROverview />
          }
        ]
      },
      {
        path: 'walnut/plans',
        children: [
          {
            path: '',
            element: <WalnutPlans />
          },
          {
            path: ':planId',
            element: <WalnutPlanDetails />
          },
          {
            path: ':planId/edit',
            element: <WalnutPlanEdit />
          },
          {
            path: 'create',
            element: <WalnutPlanCreate />
          }
        ]
      },
      {
        path: 'walnut/coupons',
        children: [
          {
            path: '',
            element: <WalnutCoupons />
          },
          {
            path: ':couponId',
            element: <WalnutCouponDetails />
          },
          {
            path: ':couponId/edit',
            element: <WalnutCouponEdit />
          },
          {
            path: 'create',
            element: <WalnutCouponCreate />
          }
        ]
      },
      {
        path: 'walnut/bills',
        children: [
          {
            path: '',
            element: <WalnutBills />
          },
          {
            path: ':billId',
            element: <WalnutBillDetails />
          },
          {
            path: ':billId/refund',
            element: <WalnutBillRefund />
          }
        ]
      },
      {
        path: 'nmi/plans',
        children: [
          {
            path: '',
            element: (
              <RequireRoles roles={['admin', 'super-admin']}>
                <NMIPlans />
              </RequireRoles>
            )
          },
          {
            path: 'create',
            element: (
              <RequireRoles roles={['admin', 'super-admin']}>
                <NMIPlanCreate />
              </RequireRoles>
            )
          },
          {
            path: ':planId',
            element: (
              <RequireRoles roles={['admin', 'super-admin']}>
                <NMIPlanDetails />
              </RequireRoles>
            )
          },
          {
            path: ':planId/edit',
            element: (
              <RequireRoles roles={['admin', 'super-admin']}>
                <NMIPlanEdit />
              </RequireRoles>
            )
          }
        ]
      },
      {
        path: 'nmi/coupons',
        children: [
          {
            path: '',
            element: <NMICoupons />
          },
          {
            path: ':couponId',
            element: <NMICouponDetails />
          },
          {
            path: ':couponId/edit',
            element: <NMICouponEdit />
          },
          {
            path: 'create',
            element: <NMICouponCreate />
          }
        ]
      },
      {
        path: 'orders',
        children: [
          {
            path: '',
            element: <NMIOrders />
          },
          {
            path: ':orderId',
            element: <NMIOrderDetails />
          },
          {
            path: ':orderId/refund',
            element: <NMIOrderRefund />
          }
        ]
      },
      {
        path: 'collections',
        children: [
          {
            path: '',
            element: <NMICollections />
          }
        ]
      },
      {
        path: 'squareup/orders',
        children: [
          {
            path: '',
            element: <SquareupOrders />
          }
        ]
      },
      {
        path: 'squareup/plans',
        children: [
          {
            path: '',
            element: <SquareupPlans />
          }
        ]
      },
      {
        path: 'stripe/orders',
        children: [
          {
            path: '',
            element: <StripeOrders />
          }
        ]
      },
      {
        path: 'stripe/coupons',
        children: [
          {
            path: '',
            element: <StripeCoupons />
          }
        ]
      },
      {
        path: 'stripe/plans',
        children: [
          {
            path: '',
            element: <StripePlans />
          }
        ]
      },
      {
        path: 'purchases',
        children: [
          {
            path: '',
            element: <PurchasesList />
          }
        ]
      },
      {
        path: 'funnel-browser',
        children: [
          {
            path: '',
            element: (
              <RequireRoles roles={['admin', 'super-admin', 'marketing']}>
                <FunnelBrowser />
              </RequireRoles>
            ),
          },
          {
            path: ':formName/:version',
            element: (
              <RequireRoles roles={['admin', 'super-admin', 'marketing']}>
                <FormVersionDetail />
              </RequireRoles>
            ),
          },
        ],
      },
      {
        path: 'form-submissions',
        children: [
          {
            path: '',
            element: <FormSubmissionsList />
          }
        ]
      },
      {
        path: 'iop',
        children: [
          {
            path: '',
            element: <Navigate to="/dashboard/iop/leads" replace />
          },
          {
            path: 'cohorts/:cohortId',
            element: <IopCohortDetails />
          },
          {
            path: 'sessions/:sessionId',
            element: <IopSessionDetails />
          },
          {
            path: ':tab',
            element: <IopPage />
          }
        ]
      },
      {
        path: 'iop-leads',
        element: <Navigate to="/dashboard/iop/leads" replace />
      },
      {
        path: 'iop-episodes',
        children: [
          {
            path: '',
            element: <Navigate to="/dashboard/iop/episodes" replace />
          },
          {
            path: 'patient/:customerId',
            element: <IopEpisodePage />
          },
          {
            path: ':episodeId/patient-sessions/:patientSessionId',
            element: <IopPatientSessionPage />
          },
          {
            path: ':episodeId',
            element: <IopEpisodePage />
          }
        ]
      },
      {
        path: 'consultations',
        children: [
          {
            path: '',
            element: <ConsultationsList />
          },
          {
            path: ':consultationId',
            element: <ConsultationDetailsView />
          }
        ]
      },
      {
        path: 'consultation-waitlist',
        element: <ConsultationWaitlistList />
      },
      {
        path: 'stuck-consultations',
        element: (
          <RequireRoles roles={['admin', 'super-admin', 'ops', 'customer_support']}>
            <StuckConsultationsList />
          </RequireRoles>
        )
      },
      {
        path: 'async-consultations',
        children: [
          {
            path: '',
            element: <AsyncConsultationsList />
          }
        ]
      },
      {
        path: 'scheduled-actions',
        element: <ScheduledActionsList />
      },
      {
        path: 'test-kits',
        element: <TestKitsList />
      },
      {
        path: 'sales',
        element: <SalesList />
      },
      {
        path: 'live-sales',
        element: (
          <RequireRoles roles={['admin', 'super-admin']}>
            <LiveSalesDashboard />
          </RequireRoles>
        )
      },
      {
        path: 'subscription-management',
        element: <KetSubscriptionManage />
      },
      {
        path: 'p2p-referrals',
        element: <P2PReferralsList />
      },
      {
        path: 'testimonials',
        children: [
          {
            path: '',
            element: <Navigate to="/dashboard/testimonials/review" replace />
          },
          {
            path: 'invites',
            element: <TestimonialInvites />
          },
          {
            path: 'review',
            element: <TestimonialsReview />
          }
        ]
      },
      {
        path: 'content-capture',
        children: [
          {
            path: '',
            element: <Navigate to="/dashboard/content-capture/requests" replace />
          },
          {
            path: 'requests',
            element: <ContentCaptureRequests />
          },
          {
            path: 'requests/:id',
            element: <ContentCaptureRequestDetail />
          },
          {
            path: 'library',
            element: <ContentCaptureLibrary />
          },
          {
            path: 'my',
            element: <ContentCaptureMy />
          },
          {
            path: 'my/:id',
            element: <ContentCaptureRecorder />
          }
        ]
      },
      {
        path: 'iceberg-patients',
        element: <IcebergPatients />
      },
      {
        path: 'coaching',
        children: [
          {
            path: 'chats',
            element: <Navigate to="/dashboard/chats?chatType=coaching" replace />
          },
          {
            path: 'patients',
            element: (
              <RequireRoles roles={COACHING_CHAT_ROLES}>
                <CoachingPatients />
              </RequireRoles>
            )
          },
          {
            path: 'sessions',
            element: <CoachingSessionManagement />
          }
        ]
      },
      {
        path: 'group-sessions',
        element: <GroupSessionManagement />
      }
    ]
  },
  {
    path: '*',
    element: <MainLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="/dashboard" />
      },
      {
        path: '401',
        element: <AuthorizationRequired />
      },
      {
        path: '404',
        element: <NotFound />
      },
      {
        path: '500',
        element: <ServerError />
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  }
];

export default routes;
